/** The device fields the printed helper sheet shows. */
export interface HelperSheetDevice {
  deviceId: string;
  name: string;
  macAddress: string;
  macBasedSerial: string;
  secureSerial: string;
  secureCode: string;
}

// The sheet is assembled as an HTML string, so device fields need escaping.
const escapeHtml = (value: string) =>
  value.replace(
    /[&<>"']/g,
    (char) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char] as string
  );

const STYLES = `
:root {
  --ink: #15202B;
  --muted: #4A5A68;
  --rule: #DCE2E7;
  --rule-strong: #AEBAC4;
  --brand: #00558C;
  --brand-dark: #003D66;
  --zebra: #F6F8FA;
}
*, *::before, *::after {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}
body {
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: var(--ink);
  background: #fff;
  padding: 40px 32px 48px;
  print-color-adjust: exact;
  -webkit-print-color-adjust: exact;
}
.sheet {
  max-width: 940px;
  margin: 0 auto;
}
.masthead {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 40px;
  padding-bottom: 14px;
  border-bottom: 2px solid var(--brand);
}
h1 {
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.01em;
}
.lede {
  margin-top: 6px;
  max-width: 62ch;
  font-size: 13px;
  color: var(--muted);
}
.masthead-side {
  flex: none;
  text-align: right;
}
.meta {
  font-size: 12px;
  color: var(--muted);
}
.tally {
  margin-top: 2px;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}
.tally strong {
  font-weight: 600;
}
.controls {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
}
button {
  font: inherit;
  font-size: 13px;
  padding: 7px 14px;
  border: 1px solid var(--rule-strong);
  border-radius: 4px;
  background: #fff;
  color: var(--ink);
  cursor: pointer;
}
button:hover {
  border-color: var(--muted);
}
button:focus-visible {
  outline: 2px solid var(--brand);
  outline-offset: 2px;
}
.btn-print {
  background: var(--brand);
  border-color: var(--brand);
  color: #fff;
}
.btn-print:hover {
  background: var(--brand-dark);
  border-color: var(--brand-dark);
}
h2 {
  margin: 34px 0 10px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: var(--muted);
}
h2 .count {
  margin-left: 8px;
  font-size: 12px;
  letter-spacing: 0;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}
table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  /* Fixed layout so both tables share one column grid. */
  table-layout: fixed;
}
th:nth-child(3) { width: 21%; }
th:nth-child(4) { width: 23%; }
th:nth-child(5) { width: 17%; }
th:nth-child(6) { width: 14%; }
th {
  padding: 0 10px 8px;
  font-size: 0.82em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--muted);
  text-align: left;
  /* Keeps a wrapped header on the same baseline as the rest. */
  vertical-align: bottom;
  border-bottom: 1px solid var(--rule-strong);
}
td {
  padding: 9px 10px;
  border-bottom: 1px solid var(--rule);
  overflow-wrap: break-word;
}
tbody tr:nth-child(even) {
  background: var(--zebra);
}
.col-tick {
  width: 46px;
  text-align: center;
}
.col-no {
  width: 40px;
  text-align: right;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
}
.mono {
  /* Not theme.ts's fontFamilyMono: this sheet is written into a bare popup that
     never loads the JetBrains Mono webfont, so it needs OS faces only. */
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 0.95em;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
/* The MAC is what you read off the unit, so it carries the emphasis. */
.match {
  font-weight: 600;
}
input[type='checkbox'] {
  appearance: none;
  -webkit-appearance: none;
  display: block;
  width: 17px;
  height: 17px;
  margin: 0 auto;
  border: 1.5px solid var(--rule-strong);
  border-radius: 3px;
  background: #fff;
  cursor: pointer;
}
input[type='checkbox']:checked {
  background: var(--brand);
  border-color: var(--brand);
}
input[type='checkbox']:checked::after {
  content: '';
  display: block;
  width: 5px;
  height: 9px;
  margin: 1px auto 0;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
input[type='checkbox']:focus-visible {
  outline: 2px solid var(--brand);
  outline-offset: 2px;
}
.empty {
  padding: 4px 10px;
  font-size: 13px;
  color: var(--muted);
}
footer {
  margin-top: 36px;
  padding-top: 12px;
  border-top: 1px solid var(--rule);
  font-size: 12px;
  color: var(--muted);
}
@media print {
  @page {
    margin: 14mm;
  }
  body {
    padding: 0;
    font-size: 10.5pt;
  }
  .sheet {
    max-width: none;
  }
  .screen-only {
    display: none !important;
  }
  table {
    font-size: 10pt;
  }
  thead {
    display: table-header-group;
  }
  tr {
    break-inside: avoid;
  }
  h2 {
    break-after: avoid;
  }
  /* Don't print a section with nothing in it. */
  section.is-empty {
    display: none;
  }
}`;

// Ticking a row moves it between the two tables; nothing is persisted.
const SCRIPT = `
function setSection(name, rowCount) {
  document.getElementById(name + 'Count').textContent = rowCount;
  document.getElementById(name + 'Table').hidden = rowCount === 0;
  document.getElementById(name + 'Empty').hidden = rowCount !== 0;
  document.getElementById(name + 'Section').classList.toggle('is-empty', rowCount === 0);
}

function refresh() {
  const done = document.querySelectorAll('#completedBody tr').length;
  setSection('pending', document.querySelectorAll('#pendingBody tr').length);
  setSection('completed', done);
  document.getElementById('tallyDone').textContent = done;
}

function toggleDevice(checkbox) {
  const row = checkbox.closest('tr');
  document.getElementById(checkbox.checked ? 'completedBody' : 'pendingBody').appendChild(row);
  refresh();
}

function resetAll() {
  if (!confirm('Clear all ticks?')) {
    return;
  }

  document.querySelectorAll('#completedBody tr').forEach(row => {
    row.querySelector('input[type="checkbox"]').checked = false;
    document.getElementById('pendingBody').appendChild(row);
  });

  refresh();
}

window.onload = refresh;`;

// Both sections share these columns.
const TABLE_HEAD = `
        <thead>
          <tr>
            <th scope="col" class="col-tick">Done</th>
            <th scope="col" class="col-no">#</th>
            <th scope="col">Device</th>
            <th scope="col">MAC address</th>
            <th scope="col">MAC-based serial</th>
            <th scope="col">Secure serial</th>
            <th scope="col">Secure code</th>
          </tr>
        </thead>`;

const renderRows = (devices: HelperSheetDevice[]) =>
  devices
    .map(
      (device, index) => `
            <tr>
              <td class="col-tick">
                <input type="checkbox" aria-label="Mark ${escapeHtml(device.name)} as labelled" onchange="toggleDevice(this)">
              </td>
              <td class="col-no">${index + 1}</td>
              <td>${escapeHtml(device.name)}</td>
              <td class="mono match">${escapeHtml(device.macAddress)}</td>
              <td class="mono">${escapeHtml(device.macBasedSerial)}</td>
              <td class="mono">${escapeHtml(device.secureSerial)}</td>
              <td class="mono">${escapeHtml(device.secureCode)}</td>
            </tr>`
    )
    .join('');

/**
 * Builds the standalone bench worksheet written into the helper-sheet window.
 * Devices are ordered by MAC-based serial — the order they sit in on the bench.
 */
export const buildHelperSheetHtml = (devices: HelperSheetDevice[]): string => {
  const ordered = [...devices].sort(
    (a, b) => (parseInt(a.macBasedSerial) || 0) - (parseInt(b.macBasedSerial) || 0)
  );
  const generatedAt = new Date().toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <title>Device label helper sheet</title>
    <meta charset="UTF-8">
    <style>${STYLES}
    </style>
  </head>
  <body>
    <div class="sheet">
      <header class="masthead">
        <div>
          <h1>Device label helper sheet</h1>
          <p class="lede">
            Find a unit by its MAC address, apply the printed label, then tick the row.
            <span class="screen-only">Ticked rows move down to <em>Labelled</em>.</span>
          </p>
        </div>
        <div class="masthead-side">
          <p class="meta">${ordered.length} device${ordered.length === 1 ? '' : 's'} &middot; ${generatedAt}</p>
          <p class="tally"><strong id="tallyDone">0</strong> of ${ordered.length} labelled</p>
          <div class="controls screen-only">
            <button type="button" class="btn-print" onclick="window.print()">Print</button>
            <button type="button" onclick="resetAll()">Clear ticks</button>
          </div>
        </div>
      </header>

      <section id="pendingSection">
        <h2>To label <span class="count" id="pendingCount">${ordered.length}</span></h2>
        <table id="pendingTable">${TABLE_HEAD}
          <tbody id="pendingBody">${renderRows(ordered)}
          </tbody>
        </table>
        <p class="empty" id="pendingEmpty" hidden>Every selected device is labelled.</p>
      </section>

      <section id="completedSection">
        <h2>Labelled <span class="count" id="completedCount">0</span></h2>
        <table id="completedTable" hidden>${TABLE_HEAD}
          <tbody id="completedBody"></tbody>
        </table>
        <p class="empty" id="completedEmpty">Nothing ticked yet.</p>
      </section>

      <footer class="screen-only">Ticks are not saved — closing this tab clears them.</footer>
    </div>

    <script>${SCRIPT}
    </script>
  </body>
</html>`;
};
